import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Smooth-scrolls the viewport to `#hash` targets when the URL contains one.
 * Works both on initial load and on React Router client-side navigations
 * (`<Link to="/hr#services">`). Mount once inside <BrowserRouter>.
 *
 * The 2-frame RAF delay is necessary because a client-side navigation renders
 * the destination route before the DOM has painted — the target element may
 * not exist yet when the effect fires.
 */
export default function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      // No hash → scroll top on a normal route change so users don't land
      // mid-page after clicking a nav link.
      window.scrollTo({ top: 0 });
      return;
    }
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) return;

    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      // Section not mounted yet — retry a handful of times over ~500ms
      if (attempts++ < 20) requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(() => requestAnimationFrame(tryScroll));
  }, [pathname, hash]);

  return null;
}
